import { useTheme } from '../../styles/ThemeContext';
import { fmtEur } from '../../utils/helpers';

/**
 * Step 4: Print Preview Layout
 * Note: The print-container always uses white background for printing
 */
export function Step4Preview({ sd, units, products, orderNumber, totalQty, totalValue, onBack, onPrint, onNext }) {
  const { theme } = useTheme();
  const c = theme.colors;
  const today = new Date().toLocaleDateString('en-GB');

  return (
    <div>
      <div className="no-print" style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginBottom: 18 }}>
        <button onClick={onBack} style={theme.btnG}>← Back to editing</button>
        <button onClick={onPrint} style={{ ...theme.btnG, borderColor: c.accent, color: c.accent }}>🖨 Print / PDF</button>
        <button onClick={onNext} style={theme.btnP}>Next: Confirm →</button>
      </div>

      {/* Print container - always white background for printing */}
      <div className="print-container" style={{ background: '#fff', color: '#111', padding: '32px 40px', borderRadius: 4, fontFamily: "'Segoe UI',sans-serif", maxWidth: 860, margin: '0 auto' }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', borderBottom: '3px solid #111', paddingBottom: 16, marginBottom: 18 }}>
          <div>
            <div style={{ fontSize: 26, fontWeight: 700, letterSpacing: -0.5 }}>PACKING LIST</div>
            <div style={{ fontSize: 11, color: '#666', marginTop: 2 }}>Transport Management System — GPS Activities</div>
            <div style={{ marginTop: 6, fontFamily: 'monospace', fontSize: 10, color: '#888' }}>Ref: <b style={{ color: '#111' }}>{orderNumber}</b></div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 10, color: '#888', fontFamily: 'monospace' }}>DATE</div>
            <div style={{ fontSize: 14, fontWeight: 700 }}>{today}</div>
            {sd.preparedBy && (
              <>
                <div style={{ fontSize: 10, color: '#888', fontFamily: 'monospace', marginTop: 5 }}>PREPARED BY</div>
                <div style={{ fontWeight: 600, fontSize: 13 }}>{sd.preparedBy}</div>
              </>
            )}
          </div>
        </div>

        {/* Project Information */}
        {(sd.deptNumber || sd.projectNumber) && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 9, fontFamily: 'monospace', letterSpacing: 1.5, color: '#d48a00', borderBottom: '1px solid #e0e0e0', paddingBottom: 4, marginBottom: 8 }}>Project Information</div>
            <div style={{ display: 'flex', gap: 24, fontFamily: 'monospace', fontSize: 11 }}>
              {sd.deptNumber && <div><span style={{ color: '#999' }}>DEPT# </span>{sd.deptNumber}</div>}
              {sd.projectNumber && <div><span style={{ color: '#999' }}>PROJECT# </span>{sd.projectNumber}</div>}
              {sd.pickupDate && <div><span style={{ color: '#999' }}>PICKUP </span>{sd.pickupDate}</div>}
              {sd.deliveryDate && <div><span style={{ color: '#999' }}>DELIVERY </span>{sd.deliveryDate}</div>}
            </div>
          </div>
        )}

        {/* Addresses */}
        {(sd.collCompany || sd.delCompany) && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 9, fontFamily: 'monospace', letterSpacing: 1.5, color: '#d48a00', borderBottom: '1px solid #e0e0e0', paddingBottom: 4, marginBottom: 8 }}>Addresses</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
              {[
                ['Collection', [sd.collCompany, sd.collAddress, `${sd.collPostal} ${sd.collCity}`.trim(), sd.collCountry, sd.collContact, sd.collPhone, sd.collEmail]],
                ['Delivery', [sd.delCompany, sd.delAddress, `${sd.delPostal} ${sd.delCity}`.trim(), sd.delCountry, sd.delContact, sd.delPhone, sd.delEmail]]
              ].map(([t, lines]) => (
                <div key={t}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>{t}</div>
                  <div style={{ fontSize: 12, lineHeight: 1.7 }}>{lines.filter(Boolean).map((l, i) => <div key={i}>{l}</div>)}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Shipping Units & Products */}
        <div style={{ fontSize: 9, fontFamily: 'monospace', letterSpacing: 1.5, color: '#d48a00', borderBottom: '1px solid #e0e0e0', paddingBottom: 4, marginBottom: 12 }}>Shipping Units & Products</div>
        {units.map((unit, ui) => {
          const unitProducts = products.filter((p) => p.unitId === unit.id);
          return (
            <div key={unit.id} className="shipping-unit" style={{ marginBottom: 16 }}>
              <div style={{ background: '#f4f4f4', padding: '7px 8px', fontWeight: 600, fontSize: 11, fontFamily: 'monospace', borderBottom: '1px solid #ddd', display: 'flex', gap: 10, alignItems: 'center' }}>
                <span style={{ color: '#d48a00' }}>#{String(ui + 1).padStart(2, '0')}</span>
                <span>{unit.type} — Unit {unit.number}</span>
                <span style={{ color: '#888', fontWeight: 400, marginLeft: 'auto', fontSize: 10 }}>
                  {unit.length}×{unit.width}×{unit.height} cm · Tare: {unit.tare}kg{unit.totalWeight ? ` · Total: ${unit.totalWeight}kg` : ''}
                  <span style={{ marginLeft: 8, padding: '2px 6px', borderRadius: 2, background: '#eee', color: '#333', fontSize: 9, fontFamily: 'monospace' }}>{unit.export_type || 'Temporary'}</span>
                </span>
              </div>
              {unitProducts.length > 0 ? (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                  <thead>
                    <tr style={{ background: '#111' }}>
                      {['#', 'Category', 'Product', 'EQ #', 'Qty', 'CoO', 'HS Code', 'Unit €', 'Total €', 'DG'].map((h) => (
                        <th key={h} style={{ textAlign: 'left', padding: '5px 7px', color: '#fff', fontSize: 9, fontFamily: 'monospace', letterSpacing: 0.5 }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {unitProducts.map((p, i) => (
                      <tr key={p.id} style={{ borderBottom: '1px solid #eee', background: i % 2 ? '#fafafa' : '#fff' }}>
                        <td style={{ padding: '5px 7px', fontFamily: 'monospace', color: '#999', fontSize: 10 }}>{String(i + 1).padStart(2, '0')}</td>
                        <td style={{ padding: '5px 7px', color: '#666' }}>{p.category}</td>
                        <td style={{ padding: '5px 7px', fontWeight: 500 }}>{p.product}</td>
                        <td style={{ padding: '5px 7px', fontFamily: 'monospace' }}>{p.eqNumber || ''}</td>
                        <td style={{ padding: '5px 7px', fontFamily: 'monospace', textAlign: 'center' }}>{p.qty}</td>
                        <td style={{ padding: '5px 7px', fontFamily: 'monospace' }}>{p.coo}</td>
                        <td style={{ padding: '5px 7px', fontFamily: 'monospace' }}>{p.hsCode}</td>
                        <td style={{ padding: '5px 7px', fontFamily: 'monospace' }}>€{fmtEur(p.itemPrice)}</td>
                        <td style={{ padding: '5px 7px', fontFamily: 'monospace', fontWeight: 600 }}>€{fmtEur(p.itemPrice * p.qty)}</td>
                        <td style={{ padding: '5px 7px' }}>{p.dg === 'Yes' && <span style={{ color: '#c0392b', fontWeight: 700, fontSize: 9, fontFamily: 'monospace' }}>DG</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div style={{ padding: 10, background: '#f9f9f9', color: '#999', fontSize: 11, border: '1px solid #ddd', borderTop: 'none', fontFamily: 'monospace' }}>No products in this unit</div>
              )}
            </div>
          );
        })}

        {/* Summary */}
        <div className="print-summary" style={{ background: '#111', color: '#fff', padding: '14px 18px', marginTop: 16, display: 'flex', gap: 32, flexWrap: 'wrap' }}>
          {[['Total Units', units.length], ['Products', products.length], ['Total Qty', totalQty], ['Total Value', `€${fmtEur(totalValue)}`], ['DG Items', products.filter((p) => p.dg === 'Yes').length]].map(([l, v]) => (
            <div key={l}>
              <div style={{ fontSize: 9, color: '#777', fontFamily: 'monospace', letterSpacing: 1 }}>{l}</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: l === 'DG Items' && products.some((p) => p.dg === 'Yes') ? '#f85149' : '#f0a500', fontFamily: 'monospace' }}>{v}</div>
            </div>
          ))}
        </div>

        <div className="print-footer" style={{ marginTop: 16, fontSize: 10, color: '#bbb', textAlign: 'center', fontFamily: 'monospace' }}>
          Generated by TMS Packing List Generator · {orderNumber}
        </div>
      </div>
    </div>
  );
}
